import { useCallback, useEffect, useMemo, useState } from 'react'

type MobileModeOverride = 'auto' | 'mobile' | 'desktop'

const STORAGE_KEY = 'trafficplay.mobileOperatorMode'
const MOBILE_QUERY = 'mobile'
const DESKTOP_QUERY = 'desktop'
const MOBILE_BREAKPOINT = 768

function readInitialOverride(): MobileModeOverride {
  if (typeof window === 'undefined') return 'auto'

  const params = new URLSearchParams(window.location.search)
  if (params.get(MOBILE_QUERY) === '1') return 'mobile'
  if (params.get(DESKTOP_QUERY) === '1') return 'desktop'

  const saved = window.localStorage.getItem(STORAGE_KEY)
  return saved === 'mobile' || saved === 'desktop' ? saved : 'auto'
}

function readSmallViewport(): boolean {
  if (typeof window === 'undefined') return false
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
}

export function useMobileOperatorMode() {
  const [override, setOverrideState] = useState<MobileModeOverride>(readInitialOverride)
  const [isSmallViewport, setIsSmallViewport] = useState(readSmallViewport)

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`)
    const update = () => setIsSmallViewport(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [])

  const setOverride = useCallback((next: MobileModeOverride) => {
    setOverrideState(next)
    if (next === 'auto') {
      window.localStorage.removeItem(STORAGE_KEY)
    } else {
      window.localStorage.setItem(STORAGE_KEY, next)
    }
  }, [])

  const isMobileMode = useMemo(() => {
    if (override === 'mobile') return true
    if (override === 'desktop') return false
    return isSmallViewport
  }, [isSmallViewport, override])

  return {
    isMobileMode,
    override,
    setOverride,
    isSmallViewport,
  }
}
